import type { CSSProperties } from "react";
import type { BenchmarkMetric, BenchmarkTable as Table } from "@/api/types";

const METRIC = /^(?<figure>[\d.,]+\s?%?)\s*(?<unit>.*)$/;
const PERCENT = /^(?<value>\d+(?:\.\d+)?)\s?%$/;

/** "68.1%" → 0.681; anything that is not a bare percentage → null. */
export function percentOf(cell: string | number): number | null {
  if (typeof cell !== "string") return null;
  const value = PERCENT.exec(cell.trim())?.groups?.value;
  return value === undefined ? null : Math.min(Number(value) / 100, 1);
}

const TERMS: Record<string, string> = {
  answerable:
    "Answerable: the gallery holds another photograph of the same shark and flank, so there is a right answer to find.",
  "rank-1": "Rank-1: the right shark is the first candidate.",
  "rank-5": "Rank-5: it is among the first five.",
  "rank-10": "Rank-10: it is among the first ten.",
  mrr: "MRR: the mean of 1 / rank.",
};

/** Definitions for the table's columns that need one, in column order. */
export function glossary(columns: readonly string[]): string[] {
  return columns.flatMap((c) => TERMS[c.trim().toLowerCase()] ?? []);
}

/** "95% rank-1" → a large "95%" with "rank-1" set small beside it; other values as given. */
function MetricValue({ value }: { value: string }) {
  const { figure, unit } = METRIC.exec(value)?.groups ?? { figure: value, unit: "" };
  return (
    <span style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
      <span className="metric-figure num">{figure}</span>
      {unit && <span className="label">{unit}</span>}
    </span>
  );
}

/** The benchmark's headline metrics as large readouts in a hairline row. */
export function BenchmarkMetrics({ metrics }: { metrics: readonly BenchmarkMetric[] }) {
  return (
    <div className="steps metrics" style={{ "--n": metrics.length } as CSSProperties}>
      {metrics.map((m) => (
        <div key={m.label} className="step">
          <span className="label">{m.label}</span>
          <MetricValue value={m.value} />
          {m.note && (
            <span className="muted" style={{ fontSize: 14 }}>
              {m.note}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** The benchmark's full table; bare percentages get a bar, so columns read at a glance. */
export function BenchmarkTable({ table }: { table: Table }) {
  return (
    <div className="scroll-x">
      <table className="table bench-table">
        {table.caption && <caption className="label">{table.caption}</caption>}
        <thead>
          <tr>
            {table.columns.map((c, j) => (
              <th key={c} scope="col" className={j === 0 ? undefined : "r"}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => {
                if (j === 0) {
                  return (
                    <th key={j} scope="row">
                      {cell}
                    </th>
                  );
                }
                const share = percentOf(cell);
                return (
                  <td key={j} className="r mono">
                    {typeof cell === "number" ? cell.toLocaleString("en-GB") : cell}
                    {share !== null && (
                      <span className="cell-bar" aria-hidden="true">
                        <span style={{ width: `${share * 100}%` }} />
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
